import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { FiClock, FiUser, FiVideo } from 'react-icons/fi';
import PocketBase from "pocketbase";
import { IoCalendar } from "react-icons/io5";

const pb = new PocketBase(process.env.REACT_APP_POCKETBASE_URL);

const ClassScheduleViewer = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [, setColomboDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [classSchedules, setClassSchedules] = useState([]);
  const [teachers, setTeachers] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await pb.collection('Classes').getFullList({
          sort: 'created'
        });
        setClassSchedules(response);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching class schedules:', error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    setColomboDate(selectedDate.toLocaleDateString('en-US', options));
  }, [selectedDate]);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const teacherPromises = classSchedules.map(async (cls) => {
          if (!teachers[cls.teacher]) {
            const teacher = await pb.collection('Teachers').getOne(cls.teacher);
            return {
              id: cls.teacher,
              name: teacher.name,
              avatar: teacher.avatar,
              collectionId:teacher.collectionId
            };
          }
        });

        const teacherData = await Promise.all(teacherPromises);
        const teacherInfo = teacherData.reduce((acc, teacher) => {
          if (teacher) acc[teacher.id] = teacher;
          return acc;
        }, {});

        setTeachers(prevTeachers => ({ ...prevTeachers, ...teacherInfo }));
      } catch (error) {
        console.error('Error fetching teacher details:', error);
      }
    };

    if (classSchedules.length > 0) {
      fetchTeachers();
    }
  }, [classSchedules, teachers]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const formatTime = (timeString) => {
    const time = new Date(timeString);
    return time.toUTCString().split(' ')[4]; 
  };

  const filteredClasses = classSchedules.filter((cls) => {
    const classDate = new Date(cls.date).toDateString();
    const selectedDateString = selectedDate.toDateString();
    return classDate === selectedDateString;
  });

  if (isLoading) {
    return (
      <div className="p-4 max-w-md mx-auto min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading schedule...</div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-md mx-auto min-h-screen bg-gray-50">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2 text-center bg-gray-100/20 backdrop-blur-sm border border-gray-200 shadow-sm rounded-lg p-3 flex items-center justify-center gap-2">
            <IoCalendar  className="text-blue-500" />
            Class Schedule
        </h1>
        <p className="text-center text-gray-600 text-sm">
          {/* Colombo, Sri Lanka · {colomboDate} */}
        </p>
      </header>

      <div className="mb-6 bg-white rounded-xl shadow-sm p-3">
        <Calendar
          onChange={handleDateChange}
          value={selectedDate}
          calendarType="gregory"
          minDetail="month"
          next2Label={null}
          prev2Label={null}
          className="react-calendar border-0"
          tileClassName="rounded-lg hover:bg-blue-50"
        />
      </div>

      <div className="space-y-3">
        {filteredClasses.length === 0 ? (
          <div className="text-center p-6 bg-white rounded-xl text-gray-500">
            No classes scheduled today
          </div>
        ) : (
          filteredClasses.map((cls) => (
            <div
              key={cls.id}
              className="bg-white rounded-xl shadow-xs p-4 border border-gray-100"
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-gray-800 text-lg">
                  {cls.name}
                </h3>
                <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  {cls.duration} min
                </span>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <FiClock className="shrink-0" />
                  <span>{formatTime(cls.date)}</span>
                </div>

                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <FiUser className="shrink-0" />
                    {teachers[cls.teacher] ? (
                      <span className="text-gray-600">{teachers[cls.teacher].name}</span>
                    ) : (
                      <span>Loading teacher...</span>
                    )}
                  </div>
                  {teachers[cls.teacher]?.avatar && (
                    <img
                      src={`${process.env.REACT_APP_POCKETBASE_URL}/api/files/${teachers[cls.teacher].collectionId}/${teachers[cls.teacher].id}/${teachers[cls.teacher].avatar}`}
                      alt={teachers[cls.teacher].name}
                      className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                    />
                  )}
                </div>
              </div>

              <a
                href={cls.zoom}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors no-underline"
              >
                <FiVideo className="shrink-0" />
                Join Zoom Class
              </a>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ClassScheduleViewer;
